import React, { useState } from 'react';
import {
    Card,
    CardBody,
    CardFooter,
    Typography,
    Button,
    Chip,
    Collapse,
    Tabs,
    TabsHeader,
    TabsBody,
    Tab,
    TabPanel,
    Spinner,
    Tooltip,
} from "@material-tailwind/react";
import {
    Calendar,
    DollarSign,
    MapPin,
    Clock,
    FileText,
    ChevronRight,
    ChevronDown,
    Info,
    Package,
    Building,
    CalendarDays,
    Briefcase,
    RefreshCw,
    Search,
    Clipboard,
    AlertCircle,
    XIcon
} from "lucide-react";
import moment from 'moment';
import { useGetUserOwnContracts } from '../../custom-hooks/use-contracts';
import { convertToPresentableText } from '../../utils/functions';
import ErrorX from '../../components/error-x';

// Helper function to get status color
const getStatusColor = (status) => {
    switch (status) {
        case 'approved':
            return 'green';
        case 'pending':
            return 'amber';
        case 'rejected':
            return 'red';
        case 'cancelled':
            return 'red';
        case 'completed':
            return 'blue';
        default:
            return 'gray';
    }
};

// Helper function to format dates
const formatDate = (date) => {
    if (!date) return 'Ongoing';
    return moment(date).format('MMM DD, YYYY');
};

// Helper function to format contract duration
const formatDuration = (startDate, endDate) => {
    if (!endDate) return 'Permanent';

    const start = moment(startDate);
    const end = moment(endDate);
    const months = end.diff(start, 'months');

    return `${months} month${months !== 1 ? 's' : ''}`;
};



// Main component
const UserContractsPage = () => {
    const { data: contracts, isLoading, isError, refetch } = useGetUserOwnContracts();
    const [activeTab, setActiveTab] = useState("active");
    const [searchQuery, setSearchQuery] = useState('');

    // Filter contracts by search query
    const filteredContracts = contracts?.filter(contract => {
        if (!searchQuery) return true;

        const searchLower = searchQuery.toLowerCase();
        return (
            contract._id.toLowerCase().includes(searchLower) ||
            (contract.comment && contract.comment.toLowerCase().includes(searchLower)) ||
            (contract.user?.name && contract.user.name.toLowerCase().includes(searchLower)) ||
            (contract.contractType && contract.contractType.toLowerCase().includes(searchLower))
        );
    }) || [];

    // Filter contracts by status
    const activeContracts = filteredContracts.filter(contract =>
        contract.status === 'approved' &&
        (!contract.endDate || new Date(contract.endDate) > new Date()) &&
        (!contract.void || !contract.void.isVoid)
    );

    const completedContracts = filteredContracts.filter(contract =>
        contract.status === 'completed' ||
        (contract.endDate && new Date(contract.endDate) <= new Date())
    );

    const pendingContracts = filteredContracts.filter(contract =>
        contract.status === 'pending'
    );

    const cancelledContracts = filteredContracts.filter(contract =>
        contract.status === 'rejected' ||
        contract.status === 'cancelled' ||
        (contract.void && contract.void.isVoid)
    );

    // Determine which contracts to display based on active tab
    const contractsToDisplay = () => {
        switch (activeTab) {
            case 'active':
                return activeContracts;
            case 'pending':
                return pendingContracts;
            case 'completed':
                return completedContracts;
            case 'cancelled':
                return cancelledContracts;
            default:
                return activeContracts;
        }
    };

    const title = <div className="text-2xl font-thin">My Contracts</div>;
    if (isLoading) {
        return (
            <div className="p-4 md:p-6">
                {title}
                <LoadingSkeleton />
            </div>
        );
    }

    if (isError) {
        return (
            <ErrorX
                title="Error loading contracts"
                message="There was an error loading your contracts. Please try again later."
                onRetry={refetch}
            />
        );
    }

    return (
        <div className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                {title}
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    <div className="relative flex items-center w-full sm:w-64">
                        <input
                            type="text"
                            placeholder="Search contracts..."
                            className="w-full py-2 pl-9 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Search className="absolute left-2 h-5 w-5 text-gray-400" />
                        {searchQuery && (
                            <button
                                className="absolute right-2 text-gray-400 hover:text-gray-600"
                                onClick={() => setSearchQuery('')}
                            >
                                <XIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    <Tooltip content="Request a new contract (Coming soon)">
                        <Button color="blue" className="flex items-center gap-2 rounded-full" >
                            <Clipboard className="h-4 w-4" />
                            Request New Contract
                        </Button>
                    </Tooltip>
                </div>
            </div>

            <Tabs value={activeTab} onChange={(value) => setActiveTab(value)}>
                <TabsHeader>
                    <Tab value="active">
                        <div className="flex items-center gap-2">
                            <span>Active</span>
                            {activeContracts.length > 0 && (
                                <span className="bg-green-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                    {activeContracts.length}
                                </span>
                            )}
                        </div>
                    </Tab>
                    <Tab value="pending" className="flex items-center gap-2">
                        <div className="flex items-center gap-2">
                            <span>Pending</span>
                            {pendingContracts.length > 0 && (
                                <span className="bg-amber-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                    {pendingContracts.length}
                                </span>
                            )}
                        </div>
                    </Tab>
                    <Tab value="completed">
                        <div className="flex items-center gap-2">
                            <span>Completed</span>
                        </div>
                    </Tab>
                    <Tab value="cancelled">
                        <div className="flex items-center gap-2">
                            <span>Cancelled</span>
                        </div>
                    </Tab>
                </TabsHeader>
                <TabsBody>
                    <TabPanel value="active">
                        {activeContracts.length > 0 ? (
                            activeContracts.map(contract => (
                                <ContractCard key={contract._id} contract={contract} />
                            ))
                        ) : (
                            <EmptyState
                                message={searchQuery ? "No matching active contracts found" : "You don't have any active contracts"}
                                icon={searchQuery ? Search : Building}
                            />
                        )}
                    </TabPanel>

                    <TabPanel value="pending">
                        {pendingContracts.length > 0 ? (
                            pendingContracts.map(contract => (
                                <ContractCard key={contract._id} contract={contract} />
                            ))
                        ) : (
                            <EmptyState
                                message={searchQuery ? "No matching pending contracts found" : "You don't have any pending contracts"}
                                icon={searchQuery ? Search : Clock}
                            />
                        )}
                    </TabPanel>

                    <TabPanel value="completed">
                        {completedContracts.length > 0 ? (
                            completedContracts.map(contract => (
                                <ContractCard key={contract._id} contract={contract} />
                            ))
                        ) : (
                            <EmptyState
                                message={searchQuery ? "No matching completed contracts found" : "You don't have any completed contracts"}
                                icon={searchQuery ? Search : CalendarDays}
                            />
                        )}
                    </TabPanel>

                    <TabPanel value="cancelled">
                        {cancelledContracts.length > 0 ? (
                            cancelledContracts.map(contract => (
                                <ContractCard key={contract._id} contract={contract} />
                            ))
                        ) : (
                            <EmptyState
                                message={searchQuery ? "No matching cancelled contracts found" : "You don't have any cancelled contracts"}
                                icon={searchQuery ? Search : AlertCircle}
                            />
                        )}
                    </TabPanel>
                </TabsBody>
            </Tabs>

            {contractsToDisplay().length > 5 && (
                <div className="my-6 text-center">
                    <Button variant="outlined" color="blue" className="flex items-center gap-2 mx-auto">
                        <RefreshCw className="h-4 w-4" />
                        Load More
                    </Button>
                </div>
            )}
        </div>
    );
};

export default UserContractsPage;


// Component to show booking details
const BookingDetails = ({ booking }) => {
    const location = booking.office ?
        `Office 
            ${booking.office.number || booking.office._id.substring(0, 6)}
            (${booking.office?.code || 'NA'}), 
            Floor ${booking.office.floor?.number || '-'} (${booking.office.floor?.floorCode || ''}) , 
            ${booking.office.floor?.building?.name || 'Building'}` :
        booking.seat ?
            `Seat ${booking.seat.seatCode || booking.seat._id.substring(0, 6)} - 
            ${convertToPresentableText(booking.seat.seatType) || ''}, 
            Space ${booking.seat.coworking?.number || 'Coworking Space'}(${booking.seat.coworking?.code || ''}), 
            Floor ${booking.seat.coworking?.floor?.number || '-'}(${booking.seat.coworking?.floor?.floorCode || ''}) , 
            ${booking.seat.coworking?.floor?.building?.name || 'Building'}` :
            'Location not specified';

    const branch = booking.office ?
        booking.office.floor?.building?.branch?.name || 'Branch' :
        booking.seat ?
            booking.seat.coworking?.floor?.building?.branch?.name || 'Branch' :
            'Branch not specified';

    const address = booking.office ?
        booking.office.floor?.building?.branch?.address || '' :
        booking.seat ?
            booking.seat.coworking?.floor?.building?.branch?.address || '' :
            '';

    const timings = booking.dailyStartTime && booking.dailyEndTime ?
        `${moment(booking.dailyStartTime).format('h:mm A')} - ${moment(booking.dailyEndTime).format('h:mm A')}` :
        'Full Day';

    return (
        <div className={`p-4 bg-${getStatusColor(booking.status)}-50 rounded-md mb-3 border border-${getStatusColor(booking.status)}-200`}>
            <div className="flex justify-between items-start mb-2">
                <div>
                    <Typography variant="h6" className="text-gray-800">{location}</Typography>
                    <Typography variant="small" className="text-gray-500 flex items-center">
                        <MapPin className="h-3 w-3 mr-1" /> {branch} {address ? `- ${address}` : ''}
                    </Typography>
                </div>
                <Chip
                    value={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                    color={getStatusColor(booking.status)}
                    size="sm"
                />
            </div>

            <hr className="my-2 border-gray-200" />

            <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="flex items-center">
                    <Calendar className="mr-2 h-4 w-4 text-blue-500" />
                    <div>
                        <Typography variant="small" className="text-xs text-gray-500">Period</Typography>
                        <Typography variant="small" className="font-medium">{formatDate(booking.startDate)} - {formatDate(booking.endDate)}</Typography>
                    </div>
                </div>
                <div className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-blue-500" />
                    <div>
                        <Typography variant="small" className="text-xs text-gray-500">Daily Hours</Typography>
                        <Typography variant="small" className="font-medium">{timings}</Typography>
                    </div>
                </div>
                <div className="flex items-center mt-1">
                    <DollarSign className="mr-2 h-4 w-4 text-blue-500" />
                    <div>
                        <Typography variant="small" className="text-xs text-gray-500">Rent</Typography>
                        <Typography variant="small" className="font-medium">{booking.rentTotal?.toLocaleString() || '-'} {booking.rentCurrency} ({booking.rentFrequency})</Typography>
                    </div>
                </div>
                <div className="flex items-center mt-1">
                    <Briefcase className="mr-2 h-4 w-4 text-blue-500" />
                    <div>
                        <Typography variant="small" className="text-xs text-gray-500">Space Type</Typography>
                        <Typography variant="small" className="font-medium">{booking.office ? 'Private Office' : 'Coworking Seat'}</Typography>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Meeting Room Quota Component
const MeetingRoomQuota = ({ quota }) => {
    if (!quota || !quota.monthlyHours) return null;

    const usedMinutes = quota.usedHours || 0;
    const totalMinutes = quota.monthlyHours || 0;
    const dailyMinutes = quota.meetingRoomHoursPerDay || 0;
    const usedPercentage = totalMinutes > 0 ? (usedMinutes / totalMinutes) * 100 : 0;
    const lastReset = quota.lastResetDate ? moment(quota.lastResetDate).format('MMM DD, YYYY') : 'Not yet reset';

    return (
        <div className="mt-3 p-3 bg-blue-50 rounded-md">
            <Typography variant="small" className="font-medium text-blue-800 mb-1">Meeting Room Quota</Typography>
            <div className="flex justify-between mb-1">
                <Typography variant="small" className="text-blue-700">
                    {(usedMinutes / 60).toFixed(1)} / {(totalMinutes / 60).toFixed(1)} hours used
                </Typography>
                <Typography variant="small" className="text-blue-700">
                    {(dailyMinutes / 60).toFixed(1)} hours/day
                </Typography>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                <div
                    className={`h-2 rounded-full ${usedPercentage > 80 ? 'bg-red-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(usedPercentage, 100)}%` }}
                ></div>
            </div>

            <div className="flex justify-between text-xs text-blue-700">
                <span>
                    {quota.rolloverEnabled ?
                        `Rollover enabled (max ${quota.rolloverLimit} hours)` :
                        'No rollover'}
                </span>
                <span>Last reset: {lastReset}</span>
            </div>
        </div>
    );
};

// Day Pass Configuration Component
const DayPassConfig = ({ config }) => {
    if (!config || config.type === 'none') return null;

    const usedPercentage = config.maxDaysPerMonth ? (config.usedDays / config.maxDaysPerMonth) * 100 : 0;
    const lastReset = config.lastResetDate ? moment(config.lastResetDate).format('MMM DD, YYYY') : 'Not yet reset';

    return (
        <div className="mt-3 p-3 bg-purple-50 rounded-md">
            <Typography variant="small" className="font-medium text-purple-800 mb-1">Day Pass Configuration</Typography>
            <div className="flex justify-between mb-1">
                <Typography variant="small" className="text-purple-700">
                    Type: {config.type.charAt(0).toUpperCase() + config.type.slice(1)}
                </Typography>
                <Typography variant="small" className="text-purple-700">
                    {config.usedDays} / {config.maxDaysPerMonth} days used
                </Typography>
            </div>

            {/* Progress bar */}
            {config.maxDaysPerMonth > 0 && (
                <div className="w-full bg-purple-200 rounded-full h-2 mb-2">
                    <div
                        className={`h-2 rounded-full ${usedPercentage > 80 ? 'bg-red-500' : 'bg-purple-500'}`}
                        style={{ width: `${Math.min(usedPercentage, 100)}%` }}
                    ></div>
                </div>
            )}

            <div className="flex flex-wrap gap-1 mb-2">
                {config.allowedDays && config.allowedDays.map(day => (
                    <Chip
                        key={day}
                        value={day.substring(0, 3)}
                        size="sm"
                        color="purple"
                        variant="outlined"
                        className="text-xs"
                    />
                ))}
            </div>

            <div className="flex justify-between text-xs text-purple-700">
                <span>
                    {config.includesMeetingRoom ? 'Includes meeting room' : 'No meeting room included'}
                </span>
                <span>Last reset: {lastReset}</span>
            </div>
        </div>
    );
};

// Main contract card component
const ContractCard = ({ contract }) => {
    const [expanded, setExpanded] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    return (
        <Card className="mb-4 shadow-none border border-gray-200 bg-gray-50">
            <CardBody>
                <div className="flex justify-between items-start">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Chip
                                value={contract.contractType === 'permanent' ? 'Permanent' : 'Temporary'}
                                color={contract.contractType === 'permanent' ? 'blue' : 'purple'}
                                size="sm"
                            />
                            <Chip
                                value={contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
                                color={getStatusColor(contract.status)}
                                size="sm"
                            />
                        </div>
                        <div className="text-lg font-normal">
                            Contract <span className="uppercase text-gray-600">#{contract._id.substring(contract._id.length - 6)}</span>
                        </div>
                        <Typography variant="small" className="text-gray-600">
                            Created on {formatDate(contract.createdDate || contract.startDate)}
                        </Typography>
                    </div>
                    <div className="text-right">
                        <Typography variant="h5" color="blue-gray" className="font-bold">
                            {contract.totalAdvanceRent?.toLocaleString() || '-'} PKR
                        </Typography>
                        <Typography variant="small" className="text-gray-600">Advance Payment</Typography>
                    </div>
                </div>

                <hr className="my-3 border-gray-200" />

                <div className="grid grid-cols-3 gap-4 mb-3">
                    <div>
                        <Typography variant="small" className="text-xs text-gray-500">Duration</Typography>
                        <Typography className="font-medium">{formatDuration(contract.startDate, contract.endDate)}</Typography>
                    </div>
                    <div>
                        <Typography variant="small" className="text-xs text-gray-500">Start Date</Typography>
                        <Typography className="font-medium">{formatDate(contract.startDate)}</Typography>
                    </div>
                    <div>
                        <Typography variant="small" className="text-xs text-gray-500">End Date</Typography>
                        <Typography className="font-medium">{formatDate(contract.endDate)}</Typography>
                    </div>
                </div>

                {contract.noticePeriod && (
                    <div className="mb-3">
                        <Typography variant="small" className="flex items-center text-gray-600">
                            <Info className="h-4 w-4 mr-1 text-blue-500" />
                            Notice Period: {contract.noticePeriod} Weeks
                        </Typography>
                    </div>
                )}

                {contract.comment && (
                    <div className="mb-3 bg-gray-50 p-2 rounded-md">
                        <Typography variant="small" className="flex items-center text-gray-700">
                            <FileText className="h-4 w-4 mr-1 text-blue-500" />
                            {contract.comment}
                        </Typography>
                    </div>
                )}

                {/* Show additional details if expanded */}
                <Collapse open={showDetails}>
                    {contract.reason && (
                        <div className="mt-3 bg-blue-50 p-2 rounded-md">
                            <Typography variant="small" className="flex items-center text-blue-800">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                Purpose: {contract.reason}
                            </Typography>
                        </div>
                    )}

                    {/* Meeting Room Quota */}
                    <MeetingRoomQuota quota={contract.meetingRoomQuota} />

                    {/* Day Pass Configuration */}
                    <DayPassConfig config={contract.dayPassConfig} />

                    {/* Void Information */}
                    {contract.void && contract.void.isVoid && (
                        <div className="mt-3 p-2 bg-red-50 rounded-md">
                            <Typography variant="small" className="font-medium text-red-800 mb-1">Contract Voided</Typography>
                            <Typography variant="small" className="text-red-700">
                                Date: {formatDate(contract.void.voidDate)}
                            </Typography>
                            {contract.void.voidReason && (
                                <Typography variant="small" className="text-red-700">
                                    Reason: {contract.void.voidReason}
                                </Typography>
                            )}
                        </div>
                    )}
                </Collapse>

                {/* Toggle details button */}
                {(contract.reason || contract.meetingRoomQuota?.monthlyHours > 0 ||
                    (contract.dayPassConfig && contract.dayPassConfig.type !== 'none') ||
                    (contract.void && contract.void.isVoid)) && (
                        <Button
                            variant="text"
                            size="sm"
                            onClick={() => setShowDetails(!showDetails)}
                            className="mt-2 flex items-center gap-1 p-0"
                        >
                            {showDetails ? "Hide details" : "Show more details"}
                            {showDetails ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                    )}
            </CardBody>

            <CardFooter className="pt-0">
                <Button
                    variant="text"
                    onClick={() => setExpanded(!expanded)}
                    className="w-full flex justify-center items-center"
                    color="blue"
                >
                    {expanded ? (
                        <>
                            <span>Hide Booking Details</span>
                            <ChevronDown className="ml-1 h-4 w-4" />
                        </>
                    ) : (
                        <>
                            <span>View Booking Details {contract.bookings && contract.bookings.length > 1 ? `(${contract.bookings.length})` : ''}</span>
                            <ChevronRight className="ml-1 h-4 w-4" />
                        </>
                    )}
                </Button>

                <Collapse open={expanded}>
                    <div className="mt-3">
                        <hr className="my-2 border-gray-200" />
                        <Typography variant="small" className="mb-2 font-semibold">Booking Details</Typography>
                        {contract.bookings && contract.bookings.length > 0 ? (
                            contract.bookings.map(booking => (
                                <BookingDetails key={booking._id} booking={booking} />
                            ))
                        ) : (
                            <div className="p-4 bg-gray-50 rounded-md text-center">
                                <Typography variant="small" className="text-gray-600">No booking details available</Typography>
                            </div>
                        )}
                    </div>
                </Collapse>
            </CardFooter>
        </Card>
    );
};

// Empty state component
const EmptyState = ({ message, icon: Icon = Package }) => (
    <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
        <Icon className="h-12 w-12 text-gray-400 mb-4" />
        <Typography variant="h6" color="gray">{message}</Typography>
        <Typography variant="small" className="text-gray-500 mt-2 text-center max-w-md">
            Your contracts will appear here once they are created. If you believe this is an error, please contact support.
        </Typography>
    </div>
);

// Loading skeleton component
const LoadingSkeleton = () => (
    <div className='flex justify-center items-center'>
        <Spinner />
    </div>
    // <div className="space-y-4">
    //     {[1, 2].map(i => (
    //         <div key={i} className="animate-pulse">
    //             <div className="h-8 bg-gray-200 rounded-lg w-1/4 mb-4"></div>
    //             <div className="h-24 bg-gray-200 rounded-lg mb-2"></div>
    //             <div className="grid grid-cols-3 gap-4 mb-4">
    //                 <div className="h-12 bg-gray-200 rounded-lg"></div>
    //                 <div className="h-12 bg-gray-200 rounded-lg"></div>
    //                 <div className="h-12 bg-gray-200 rounded-lg"></div>
    //             </div>
    //         </div>
    //     ))}
    // </div>
);